//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import classNames from 'classnames';
import React from 'react';
import I18n from 'i18n-js';

import { RCIcon, RCIconProps } from '../RCIcon/RCIcon';
import { RCImage } from '../RCImage/RCImage';
import { RCText } from '../RCText/RCText';
import { RCCloseButton } from '../RCCloseButton/RCCloseButton';
import { ThemeProps, themeModifier } from '../helpers/themedSurface';

// Re-export the shared theme types for consumers typing the theme props.
export type {
  UtilityColor,
  UtilityVariant,
  SurfaceVariant,
  TempoTheme,
} from '../helpers/themedSurface';

// Optional props use destructuring defaults rather than defaultProps.
/* eslint-disable react/require-default-props */

interface RCCalloutOwnProps {
  /** Border style. `thin` is a full hairline; `leading-accent` is a thick leading bar. Defaults to `none`. */
  border?: 'none' | 'thin' | 'leading-accent';
  /** Tighter density for short in-content messages (reduced padding, smaller text, etc.).
   * If passing a `cta`, give the button a `small` size so it matches the reduced density
   * — that's also the dismiss button's size in compact. */
  compact?: boolean;
  /** A small leading icon. Mutually exclusive with `image` (icon wins). */
  icon?: RCIconProps['icon'];
  /** Whether the icon is themed to the callout's accent (default) or matches the body text color.
   *  For an off-theme color, pass a color utility class on the icon element itself. */
  themedIcon?: boolean;
  /** A leading image. Pass a `src` string (rendered via RCImage — its default aspect
   *  constrains tall/odd SVGs, plus lazy-loading) or a rendered element directly
   *  (escape hatch, e.g. an RCImage with a custom aspect). Mutually exclusive with `icon`. */
  image?: string | React.ReactElement;
  /** Image size: `small` (48px), `medium` (default, 64px), or `large` (80px). */
  imageSize?: 'small' | 'medium' | 'large';
  /** Title — a string renders as bold utility text; or pass your own node. */
  title?: React.ReactNode;
  /** A call-to-action element, typically an RCButton or RCButtonLink. */
  cta?: JSX.Element;
  /** When provided, renders a dismiss button in the top corner; called on click. */
  onDismiss?: () => void;
  /** Announce the callout to assistive tech: `status` (polite) or `alert` (assertive). */
  role?: 'status' | 'alert';
  /** Element id (e.g. for `aria-labelledby` or anchoring). */
  id?: string;
  /** Test hook. */
  'data-testid'?: string;
}

export type RCCalloutProps = React.PropsWithChildren<RCCalloutOwnProps> & ThemeProps;

// A structured, opinionated themed message layout. Unlike RCThemedBox, it owns
// its internal layout + padding and exposes only a curated prop set (no open
// className/style passthrough) so its structure can't be subverted.
export const RCCallout = React.forwardRef<HTMLDivElement, RCCalloutProps>(
  function RCCallout({
    themeType = undefined,
    variant = undefined,
    border = 'none',
    compact = false,
    icon = undefined,
    themedIcon = true,
    image = undefined,
    imageSize = 'medium',
    title = undefined,
    cta = undefined,
    onDismiss = undefined,
    role = undefined,
    id = undefined,
    'data-testid': dataTestId = undefined,
    children = undefined,
  }, ref) {
    const dismissible = !!onDismiss;
    const ariaLive = role === 'alert' ? 'assertive' : role === 'status' ? 'polite' : undefined;

    return (
      <div
        ref={ref}
        id={id}
        data-testid={dataTestId}
        role={role}
        aria-live={ariaLive}
        className={classNames(
          'rc-callout',
          themeModifier('rc-callout', themeType, variant),
          {
            [`rc-callout--border-${border}`]: border !== 'none',
            'rc-callout--dismissible': dismissible,
            'rc-callout--compact': compact,
          },
        )}
      >
        <div className="rc-callout__inner">
          <div className="rc-callout__content-wrapper">
            {icon ? (
              <div className={classNames(
                'rc-callout__icon',
                { 'rc-callout__icon--themed': themedIcon },
              )}>
                <RCIcon icon={icon} />
              </div>
            ) : image ? (
              <div className={classNames(
                'rc-callout__image',
                { 'rc-callout__image--small': imageSize === 'small' },
                { 'rc-callout__image--large': imageSize === 'large' },
              )}>
                {renderImage(image)}
              </div>
            ) : null}

            <div className="rc-callout__content">
              <div className="rc-callout__text">
                {title &&
                  <div className="rc-callout__title">
                    {renderTitle(title, compact)}
                  </div>
                }
                <div className="rc-callout__body">
                  {children}
                </div>
              </div>
              {cta && <div className="rc-callout__cta">{cta}</div>}
            </div>
          </div>

          {dismissible && (
            <div className="rc-callout__dismiss">
              <RCCloseButton
                onClick={onDismiss}
                aria-label={I18n.t('cadence.RCCallout.closeButtonLabel')}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

function renderTitle(title: React.ReactNode, compact: boolean) {
  if (typeof title === 'string') {
    return <RCText.Utility size={compact ? '400' : '450'} weight="bold">{title}</RCText.Utility>;
  }
  return title;
}

// A `src` string is rendered through RCImage (its default aspect constrains tall
// SVGs, plus lazy-loading); a rendered element is used as-is (escape hatch).
function renderImage(image: string | React.ReactElement) {
  return typeof image === 'string' ? <RCImage src={image} /> : image;
}
